/**
 * The lookup that knows what you were just working on — and can make one.
 *
 * `ComboInput` is the control; this is the control wired to the product. It
 * answers the three questions Aaron asked of the old eight-row dropdown:
 *
 * **"What is that list based on?"** Nothing — it was the first page of an
 * alphabetical list, so a company with four hundred properties saw the eight
 * beginning with A, for ever, for everybody. Now it is what *this person* last
 * opened, newest first (`recent_records`, migration 0073), with the top of the
 * alphabetical list underneath to fill the menu. The two are separated by a
 * section heading so nobody reads them as one badly-sorted list.
 *
 * **"Shouldn't it be a longer list than 8?"** Twenty-five (`LOOKUP_PAGE`),
 * which fills the menu and scrolls once. And the footer says how many there
 * are altogether: "25 of 412 · keep typing to narrow it". Twenty-five rows
 * with nothing said about the other three hundred and eighty-seven is a list
 * that looks complete and is not — you scroll, do not find it, and conclude
 * it is not on file.
 *
 * **"It has no add-new option at the bottom to create one and auto-attach
 * it."** Now it does. The create row opens the real form — the same
 * `PropertyForm` or `ContactForm` the New button opens, not a cut-down
 * version of it — stacked over the form that asked, with what was typed
 * already in the name. Saving it selects it, and the form that asked is
 * exactly as it was left.
 *
 * ## Why the create form is loaded lazily
 *
 * Because a contact needs a property lookup and a property needs a contact
 * lookup: import them statically and the two modules form a cycle. A dynamic
 * `import()` breaks it at module-evaluation time, which is where the cycle
 * would bite, and costs one small chunk the first time somebody presses
 * Create.
 */
import { Suspense, lazy, useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  LOOKUP_PAGE, emptyView, type LookupKind, type RecentRecord,
} from '@rowted/shared'
import { ComboInput, type ComboItem, type ComboResult } from './combo.js'
import { request } from '../../lib/api.js'
import { RECORD_ICONS } from '../../frame/icons.js'

const PropertyForm = lazy(() => import('../forms/property-form.js')
  .then((module) => ({ default: module.PropertyForm })))
const ContactForm = lazy(() => import('../forms/contact-form.js')
  .then((module) => ({ default: module.ContactForm })))

/** The layer a form opened from a lookup sits on — see `SheetProps.layer`. */
const STACKED = 70

/**
 * What each kind is called, where its alphabetical page comes from, and which
 * fields that page needs to draw a row.
 *
 * Only the kinds a lookup is actually pointed at today. A kind with no entry
 * still works — it reads its recents and offers nothing underneath — which is
 * the honest behaviour for one nobody has wired a search for yet.
 */
interface KindConfig {
  /** The noun in "Create a new …". */
  thing: string
  /** The heading over the alphabetical rows. */
  heading: string
  /** The list endpoint and the fields a row is drawn from. */
  list?: {
    path: string
    fields: string[]
    sub: (row: Row) => string
    /** The same named facts the recents query attaches — see `ComboItem.meta`. */
    meta?: (row: Row) => Record<string, string>
  }
}

type Row = Record<string, unknown>

const text = (value: unknown) => (value == null ? '' : String(value))
const joined = (...parts: unknown[]) => parts.map(text).filter(Boolean).join(' · ')

const KINDS: Partial<Record<LookupKind, KindConfig>> = {
  property: {
    thing: 'property',
    heading: 'All properties',
    list: {
      path: '/properties/list',
      fields: ['id', 'name', 'addressLine1', 'city', 'accountName'],
      sub: (row) => joined(row.city, row.accountName),
      meta: (row) => {
        const found: Record<string, string> = {}
        if (row.accountName) found.accountName = text(row.accountName)
        return found
      },
    },
  },
  contact: {
    thing: 'contact',
    heading: 'All contacts',
    list: {
      path: '/contacts/list',
      fields: ['id', 'name', 'email', 'accountName'],
      sub: (row) => joined(row.accountName, row.email),
    },
  },
  /*
   * The kinds below have no list endpoint of the shape above yet, so a lookup
   * pointed at one shows this person's recents and nothing underneath. That
   * is the honest answer — better than an empty menu, and it grows into the
   * full thing the day somebody wires the search.
   */
  equipment: { thing: 'piece of equipment', heading: 'All equipment' },
  job: { thing: 'job', heading: 'All jobs' },
  visit: { thing: 'visit', heading: 'All visits' },
  invoice: { thing: 'invoice', heading: 'All invoices' },
  user: { thing: 'person', heading: 'Everyone' },
}

/** Which kinds can be made from inside a lookup, rather than only found. */
const CREATABLE: ReadonlySet<LookupKind> = new Set<LookupKind>(['property', 'contact'])

export interface RecordLookupProps {
  kind: LookupKind
  value: ComboItem | null
  onPick: (item: ComboItem) => void
  label: string
  placeholder?: string
  id?: string
  describedBy?: string
  invalid?: boolean
  disabled?: boolean
  /**
   * The typed search, when the caller has a better one than the kind's list
   * endpoint — the Salesperson combo on a job already holds every person in
   * memory, and a round trip per keystroke for a filter over forty names
   * would be silly.
   */
  search?: (query: string) => Promise<ComboItem[]> | ComboItem[]
  /** The branch a record created from here is filed under. */
  orgUnitId?: string
  /** Turn the create row off where creating one would make no sense. */
  allowCreate?: boolean
}

export function RecordLookup({
  kind, value, onPick, label, placeholder, id, describedBy, invalid, disabled,
  search, orgUnitId, allowCreate = true,
}: RecordLookupProps) {
  const config = KINDS[kind]

  /*
   * The lead glyph, memoised.
   *
   * Not fussiness: `toItem` and `look` below close over it, `ComboInput`
   * re-runs its search whenever `search` changes identity, and a fresh
   * element every render would make that an unbroken loop of requests.
   */
  const lead: ReactNode = useMemo(() => {
    const Icon = RECORD_ICONS[kind === 'user' ? 'contact' : kind]
    return <Icon size={14} />
  }, [kind])

  /*
   * The create row's promise, held open while the nested form is up.
   *
   * `ComboInput` awaits what `onCreate` returns, so resolving it is how the
   * new record gets picked. Cancelling resolves it with null, which leaves
   * the field exactly as it was.
   */
  const [creating, setCreating] =
    useState<{ name: string; settle: (item: ComboItem | null) => void } | null>(null)

  /** One row, drawn the same whichever half of the list it came from. */
  const toItem = useCallback(
    (
      row: { id: string; name: string; sub?: string | null; meta?: Record<string, string> | null },
      group?: string,
    ): ComboItem => ({
      id: row.id,
      name: row.name,
      sub: row.sub || undefined,
      meta: row.meta ?? undefined,
      lead,
      group,
    }), [lead])

  const look = useCallback(async (query: string): Promise<ComboResult> => {
    const wanted = query.trim()

    /*
     * Typed: the ordinary search, ungrouped. Recents answer "show me
     * something before I have said what I want"; once somebody has said,
     * ordering by when they last looked would bury the exact match.
     */
    if (wanted) {
      if (search) {
        return { items: (await search(wanted)).map((row) => ({ lead, ...row })) }
      }
      const page = await listPage(config, wanted, LOOKUP_PAGE)
      return {
        items: page.items.map((row) => toItem(row)),
        note: noteFor(page.items.length, page.total),
      }
    }

    /* Nothing typed: what this person last opened, then the top of the list. */
    const recent = await request<{ items: RecentRecord[] }>(
      `/records/recent?object=${kind}&limit=${LOOKUP_PAGE}`,
    ).catch(() => ({ items: [] as RecentRecord[] }))

    const seen = new Set(recent.items.map((row) => row.id))
    const head = recent.items.map((row) => toItem(row, 'Recent'))
    const room = LOOKUP_PAGE - head.length
    if (room <= 0) return { items: head }

    const heading = config?.heading ?? 'Everything'

    /*
     * The rest, asked for with the recents' worth of extra rows so that
     * dropping the duplicates still leaves a full menu.
     */
    if (search) {
      const rest = (await search('')).filter((row) => !seen.has(row.id)).slice(0, room)
      return { items: [...head, ...rest.map((row) => ({ ...row, lead, group: heading }))] }
    }

    const page = await listPage(config, '', room + seen.size)
    const rest = page.items
      .filter((row) => !seen.has(row.id))
      .slice(0, room)
      .map((row) => toItem(row, heading))

    return {
      items: [...head, ...rest],
      note: noteFor(head.length + rest.length, page.total),
    }
  }, [kind, config, search, toItem, lead])

  /**
   * Picking one is a signal in its own right — a stronger one than opening a
   * page, because it is the thing somebody is working *on*. Written without
   * waiting, and a failure is swallowed: a slightly worse dropdown tomorrow
   * is not worth a red band today.
   */
  const pick = useCallback((item: ComboItem) => {
    onPick(item)
    void request('/records/recent', { method: 'POST', body: { object: kind, id: item.id } })
      .catch(() => {})
  }, [kind, onPick])

  const startCreate = useCallback((name: string) => new Promise<ComboItem | null>((settle) => {
    setCreating({ name, settle })
  }), [])

  /**
   * The new record, read back through the same query every other row in this
   * menu came from, so it looks like one of them rather than like whatever
   * the create form happened to hold in state. Noting it as seen is what
   * makes that read possible, and it is wanted anyway — you have just made
   * this thing, it is certainly the most recent one you have touched.
   *
   * If either call fails the field still fills in, with the typed name: the
   * record exists and the form that asked must not be left looking empty.
   */
  const settleWith = useCallback(async (recordId: string) => {
    if (!creating) return
    const { name, settle } = creating
    setCreating(null)

    const made = await request('/records/recent',
      { method: 'POST', body: { object: kind, id: recordId } })
      .then(() => request<{ items: RecentRecord[] }>(
        `/records/recent?object=${kind}&limit=5`))
      .then((result) => result.items.find((row) => row.id === recordId) ?? null)
      .catch(() => null)

    settle(made ? toItem(made) : { id: recordId, name, lead })
  }, [kind, creating, toItem, lead])

  const cancelCreate = useCallback(() => {
    creating?.settle(null)
    setCreating(null)
  }, [creating])

  const canCreate = allowCreate && CREATABLE.has(kind)

  return (
    <>
      <ComboInput
        value={value}
        onPick={pick}
        search={look}
        onCreate={canCreate ? startCreate : undefined}
        createOpens
        thing={config?.thing ?? kind}
        label={label}
        placeholder={placeholder}
        id={id}
        describedBy={describedBy}
        invalid={invalid}
        disabled={disabled}
      />

      {creating && kind === 'property' && (
        <Suspense fallback={null}>
          <PropertyForm
            open
            layer={STACKED}
            orgUnitId={orgUnitId}
            initialName={creating.name}
            onClose={cancelCreate}
            onSaved={(propertyId) => void settleWith(propertyId)}
            onOpenExisting={(propertyId) => void settleWith(propertyId)}
          />
        </Suspense>
      )}

      {creating && kind === 'contact' && (
        <Suspense fallback={null}>
          <ContactForm
            open
            layer={STACKED}
            orgUnitId={orgUnitId ?? ''}
            initialName={creating.name}
            onClose={cancelCreate}
            onSaved={(contactId) => void settleWith(contactId)}
            onOpenExisting={(contactId) => void settleWith(contactId)}
          />
        </Suspense>
      )}
    </>
  )
}

/** One page of the kind's ordinary list, with how many there are altogether. */
async function listPage(
  config: KindConfig | undefined, query: string, limit: number,
): Promise<{
  items: { id: string; name: string; sub?: string; meta?: Record<string, string> }[]
  total?: number
}> {
  if (!config?.list) return { items: [] }
  const { path, fields, sub, meta } = config.list

  const result = await request<{ items: Row[]; totalCount?: number }>(path, {
    method: 'POST',
    body: {
      view: { ...emptyView(), fields },
      search: query || undefined,
      limit,
    },
  }).catch(() => ({ items: [] as Row[], totalCount: undefined }))

  return {
    items: result.items.map((row) => ({
      id: text(row.id),
      name: text(row.name),
      sub: sub(row),
      meta: meta?.(row),
    })),
    total: result.totalCount,
  }
}

/**
 * "25 of 412 · keep typing to narrow it", or nothing at all when the menu is
 * showing everything there is. Saying "3 of 3" would be noise.
 */
function noteFor(shown: number, total?: number): string | undefined {
  if (total == null || total <= shown) return undefined
  return `${shown} of ${total.toLocaleString()} · keep typing to narrow it`
}
